/**
 * Pure helpers for the FDE view: who the FDEs are, which pilots and clients
 * each one owns, and what needs doing next. Kept out of the components so the
 * rules can be unit tested.
 */

import { addPlaybookAction, addSharedChecks, createTodoList } from "./todos";

/** What the FDE does next, keyed by the pilot's enterprise playbook step. */
const NEXT_PLAYBOOK_ACTION = {
  pilot_users_identified: "Schedule the pilot kickoff with the client.",
  pilot_kickoff_scheduled: "Run the kickoff, then hold the week-one meeting.",
  week_one_meeting_complete: "Hold the week-two meeting.",
  week_two_meeting_completed: "Hold the week-three rollout meeting.",
  week_three_rollout_meeting_completed: "Complete the internal pilot review.",
  internal_pilot_review_completed:
    "Hand the review to sales for the proposal package.",
};

/** The pilot playbook as the FDE lives it, for the reference card. */
export const PILOT_PLAYBOOK_GUIDE = [
  {
    step: "Setup",
    detail:
      "Connect the client's LOS, add the 3–5 pilot users, and build their loan programs from their guidelines (FDE Playbook).",
  },
  { step: "Kickoff", detail: "Walk the pilot users through Addy on live files." },
  {
    step: "Weeks 1–2",
    detail: "Weekly call: review results, fix what broke, log notes in HubSpot.",
  },
  { step: "Week 3", detail: "Rollout meeting: agree what a full rollout looks like." },
  {
    step: "Review",
    detail: "Internal pilot review, then hand over to sales for the proposal.",
  },
];

/** Lowercase, accent-free, single-spaced — "João  melo" matches "Joao Melo". */
export function normalizeName(name) {
  return String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

const lastNameOf = (name) => normalizeName(name).split(" ").pop();

/**
 * One list of FDEs from HubSpot's dropdown (which pilots are tagged with) and
 * the Firestore roster (which clients are assigned to). The two are kept by
 * hand, so names are matched exactly first, then by a last name that is unique
 * on both sides ("Jay Melo" in HubSpot is "João melo" in the roster).
 *
 * @param {Array<{value: string, label: string}>} fdeOptions from /admin/pilots
 * @param {Array<{id: string, name: string, clients: object[]}>} engineers from /admin/pilots/fdes
 * @returns {Array<{id: string, name: string, hubspotValue: string|null,
 *   rosterName: string|null, clients: object[]|null}>}
 */
export function buildFdeDirectory(fdeOptions = [], engineers = []) {
  const entries = fdeOptions.map((option) => ({
    id: `hs:${option.value}`,
    name: option.label,
    hubspotValue: option.value,
    rosterName: null,
    clients: null,
  }));
  const countLastName = (names, lastName) =>
    names.filter((name) => lastNameOf(name) === lastName).length;
  const optionNames = fdeOptions.map((option) => option.label);
  const engineerNames = engineers.map((engineer) => engineer.name);

  engineers.forEach((engineer) => {
    const lastName = lastNameOf(engineer.name);
    const isUniqueLastName =
      countLastName(optionNames, lastName) === 1 &&
      countLastName(engineerNames, lastName) === 1;
    const match =
      entries.find(
        (entry) =>
          entry.hubspotValue &&
          normalizeName(entry.name) === normalizeName(engineer.name),
      ) ||
      (isUniqueLastName
        ? entries.find(
            (entry) => entry.hubspotValue && lastNameOf(entry.name) === lastName,
          )
        : null);

    if (match && match.rosterName === null) {
      match.rosterName = engineer.name;
      match.clients = engineer.clients || [];

      return;
    }
    entries.push({
      id: `fde:${engineer.id}`,
      name: engineer.name,
      hubspotValue: null,
      rosterName: engineer.name,
      clients: engineer.clients || [],
    });
  });

  return entries.sort((a, b) => a.name.localeCompare(b.name));
}

export function pilotsForFde(pilots, fde) {
  if (!fde?.hubspotValue) {
    return [];
  }

  return pilots.filter((pilot) => pilot.fdeValue === fde.hubspotValue);
}

/**
 * What an FDE should do next across their running pilots, most urgent first.
 *
 * @param {object[]} pilots the FDE's running pilots
 * @param {number} [now] injectable clock, for tests
 * @returns {Array<{id: string, dealId: string, pilotName: string,
 *   priority: "high"|"normal"|"low", text: string}>}
 */
export function buildFdeTodos(pilots, now = Date.now()) {
  const todos = createTodoList();

  pilots.forEach((pilot) => {
    addSharedChecks(todos, pilot, now);
    addPlaybookAction(todos, pilot, NEXT_PLAYBOOK_ACTION);
    if (pilot.subStage && !pilot.isInPilotStage) {
      todos.add(
        pilot,
        "stage",
        "low",
        `HubSpot still shows “${pilot.stage}”. Ask ${pilot.owner || "the deal owner"} to move the deal to Pilot.`,
      );
    }
  });

  return todos.list();
}

/**
 * Pilots, clients and to-dos per FDE, for the team overview.
 *
 * @param {ReturnType<typeof buildFdeDirectory>} directory
 * @param {object[]} pilots running pilots
 * @param {number} [now]
 */
export function summarizeFdeLoad(directory, pilots, now = Date.now()) {
  return directory.map((fde) => {
    const own = pilotsForFde(pilots, fde);
    const todos = buildFdeTodos(own, now);

    return {
      id: fde.id,
      name: fde.name,
      pilotCount: own.length,
      clientCount: fde.clients ? fde.clients.length : null,
      todoCount: todos.length,
      urgentCount: todos.filter((todo) => todo.priority === "high").length,
    };
  });
}
